import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './styles';
import PersonIcon from '@mui/icons-material/Person';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FolderImg from '../../../../Assets/Folder.png';

const ITEMS_PER_PAGE = 20;

export interface FolderItem {
  id: string;
  title: string;
  Lastupdated: string;
  userName: string;
  avatarUrl?: string;
}

export type FileItem = {
  id: string;
  name: string;
  url: string;
  size?: number;
  uploadedAt?: string;
  mimeType?: string;
};

interface Props {
  cardData?: FolderItem[];
  onFolderClick?: (id: string) => void;

  /** Upload-capable folders */
  canUpload?: (folder: FolderItem) => boolean;

  /** Folder-scoped file APIs */
  onFetchFiles?: (folderId: string) => Promise<FileItem[]>;
  onUploadFile?: (folderId: string, file: File) => Promise<FileItem>;
  onRemoveFile?: (folderId: string, fileId: string) => Promise<void>;
}

const humanSize = (size?: number) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
};

const FolderContainer: React.FC<Props> = ({
  cardData = [],
  onFolderClick,
  canUpload,
  onFetchFiles,
  onUploadFile,
  onRemoveFile,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<FolderItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  // Which folder card is expanded to show upload/list UI
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);

  // Per-folder caches/state
  const [filesByFolder, setFilesByFolder] = useState<Record<string, FileItem[]>>({});
  const [loadingByFolder, setLoadingByFolder] = useState<Record<string, boolean>>({});
  const [uploadingByFolder, setUploadingByFolder] = useState<Record<string, boolean>>({});
  const [errorByFolder, setErrorByFolder] = useState<Record<string, string | null>>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // If you need sorting/filtering before pagination, do it here
  const list = useMemo(() => cardData, [cardData]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPaginatedData(list.slice(startIndex, endIndex));
    setTotalPages(Math.ceil(list.length / ITEMS_PER_PAGE));
  }, [list, currentPage]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const isUploadCapable = (folder: FolderItem) => (canUpload ? canUpload(folder) : false);

  const ensureFolderFiles = async (folderId: string) => {
    if (!onFetchFiles) return;
    // if already loaded, skip
    if (filesByFolder[folderId]) return;
    try {
      setErrorByFolder((s) => ({ ...s, [folderId]: null }));
      setLoadingByFolder((s) => ({ ...s, [folderId]: true }));
      const list = await onFetchFiles(folderId);
      setFilesByFolder((s) => ({ ...s, [folderId]: list }));
    } catch (e: any) {
      setErrorByFolder((s) => ({ ...s, [folderId]: e?.message || 'Failed to load files' }));
    } finally {
      setLoadingByFolder((s) => ({ ...s, [folderId]: false }));
    }
  };

  const handleCardClick = async (folder: FolderItem) => {
    if (isUploadCapable(folder)) {
      setOpenFolderId(folder.id);
      await ensureFolderFiles(folder.id);
      return;
    }
    onFolderClick?.(folder.id);
  };

  const triggerFilePicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, folderId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingByFolder((s) => ({ ...s, [folderId]: true }));
      setErrorByFolder((s) => ({ ...s, [folderId]: null }));

      if (!onUploadFile) {
        // Demo fallback (remove when wiring API)
        const fake: FileItem = {
          id: `${Date.now()}`,
          name: file.name,
          url: '#',
          size: file.size,
          uploadedAt: new Date().toISOString(),
          mimeType: file.type,
        };
        setFilesByFolder((s) => ({ ...s, [folderId]: [fake, ...(s[folderId] || [])] }));
      } else {
        const created = await onUploadFile(folderId, file);
        setFilesByFolder((s) => ({ ...s, [folderId]: [created, ...(s[folderId] || [])] }));
      }
    } catch (err: any) {
      setErrorByFolder((s) => ({ ...s, [folderId]: err?.message || 'Upload failed' }));
    } finally {
      setUploadingByFolder((s) => ({ ...s, [folderId]: false }));
      e.target.value = '';
    }
  };

  const handleRemoveFile = async (folderId: string, fileId: string) => {
    if (!onRemoveFile) return;
    try {
      await onRemoveFile(folderId, fileId);
      setFilesByFolder((s) => ({
        ...s,
        [folderId]: (s[folderId] || []).filter((f) => f.id !== fileId),
      }));
    } catch (err: any) {
      setErrorByFolder((s) => ({ ...s, [folderId]: err?.message || 'Failed to delete file' }));
    }
  };

  return (
    <div className={classes.networkCardContainer} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box width="100%" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}>
        <Paper className={classes.cardHolder} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Grid container spacing={1}>
              {paginatedData.map((item) => {
                const uploadEnabled = isUploadCapable(item);
                const isOpen = openFolderId === item.id;
                const files = filesByFolder[item.id] || [];
                const isLoading = !!loadingByFolder[item.id];
                const isUploading = !!uploadingByFolder[item.id];
                const errorMsg = errorByFolder[item.id] ?? null;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        padding: 2,
                        backgroundColor: '#F6F8FB',
                        border: 'none',
                        boxShadow: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                      onClick={() => handleCardClick(item)}
                    >
                      {/* Folder Title */}
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Typography fontWeight={500} color="#373A45" fontSize={14}>
                          {item.title}
                        </Typography>
                      </Box>

                      {/* Last updated */}
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography fontWeight={400} color="#373A45" fontSize={14}>
                          Last Updated on: {item?.Lastupdated}
                        </Typography>
                      </Box>

                      {/* Folder Image (hidden when upload panel open) */}
                      {!isOpen && (
                        <Box
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            borderRadius: 2,
                            mb: 1.5,
                            backgroundColor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img src={FolderImg} alt="" height="80px" width="80px" />
                        </Box>
                      )}

                      {/* User Info */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center">
                          {item.avatarUrl ? (
                            <img
                              src={item.avatarUrl}
                              alt={item.userName}
                              style={{ width: 24, height: 24, borderRadius: '50%', marginRight: 8 }}
                            />
                          ) : (
                            <PersonIcon
                              sx={{ width: 24, height: 24, borderRadius: '50%', marginRight: 1, color: '#bdbdbd', background: '#fff' }}
                            />
                          )}
                          <Typography
                            fontWeight={500}
                            color="#373A45"
                            fontSize={14}
                            sx={{
                              maxWidth: 120,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={item.userName}
                          >
                            {item.userName.length > 15 ? `${item.userName.slice(0, 15)}...` : item.userName}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Upload & list panel (only for upload-enabled folders when open) */}
                      {uploadEnabled && isOpen && (
                        <Box
                          sx={{
                            mt: 1,
                            borderRadius: 2,
                            p: 1,
                            background: '#fff',
                            border: '1px solid #E6E8F0',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography fontWeight={600} fontSize={13} color="#123A6F">
                              Files
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ textTransform: 'none', borderRadius: 2 }}
                              onClick={triggerFilePicker}
                              disabled={isUploading}
                            >
                              {isUploading ? 'Uploading...' : 'Upload file'}
                            </Button>
                            <input
                              ref={fileInputRef}
                              hidden
                              type="file"
                              accept="application/pdf,image/*"
                              onChange={(e) => handleFileChange(e, item.id)}
                            />
                          </Box>

                          {isUploading && <LinearProgress sx={{ mb: 1 }} />}

                          {errorMsg && (
                            <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                              {errorMsg}
                            </Typography>
                          )}

                          {isLoading ? (
                            <Typography fontSize={13} color="#666" sx={{ py: 2, textAlign: 'center' }}>
                              Loading files…
                            </Typography>
                          ) : files.length > 0 ? (
                            <List dense disablePadding>
                              {files.map((f) => (
                                <ListItem
                                  key={f.id}
                                  secondaryAction={
                                    <Box>
                                      <Tooltip title="Open">
                                        <IconButton size="small" edge="end" component="a" href={f.url} target="_blank" rel="noreferrer">
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {onRemoveFile && (
                                        <Tooltip title="Delete">
                                          <IconButton
                                            size="small"
                                            edge="end"
                                            onClick={() => handleRemoveFile(item.id, f.id)}
                                            sx={{ ml: 0.5 }}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  }
                                  sx={{ px: 0.5 }}
                                >
                                  <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: '#EEF2FF', color: '#123A6F' }}>
                                      <InsertDriveFileIcon fontSize="small" />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                                    secondaryTypographyProps={{ fontSize: 12, color: '#666' }}
                                    primary={f.name}
                                    secondary={[
                                      f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : '',
                                      humanSize(f.size),
                                    ]
                                      .filter(Boolean)
                                      .join(' • ')}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography fontSize={13} color="#666" sx={{ py: 2, textAlign: 'center' }}>
                              No files uploaded yet.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="center"
            sx={{
              mt: 'auto',
              pt: 2,
              backgroundColor: 'white',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
            }}
          >
            {list.length > ITEMS_PER_PAGE && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
                siblingCount={2}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "8px",
                    fontWeight: 400,
                    color: "#000",
                    border: "1px solid #E0E0E0",
                    "&:hover": { backgroundColor: "#F5F5F5" },
                  },
                  "& .MuiPaginationItem-page.Mui-selected": {
                    backgroundColor: "#123A6F",
                    color: "#fff",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "#123A6F" },
                  },
                  "& .MuiPaginationItem-ellipsis": { color: "#000" },
                  "& .MuiPaginationItem-previousNext": { fontWeight: 400, color: "#000" },
                  "& .MuiPaginationItem-previousNext.Mui-disabled": { color: "#C0C0C0" },
                }}
                showFirstButton={false}
                showLastButton={false}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default FolderContainer;
